import studentApi from './studentApi';

export interface Notification {
  id: number;
  userId: number;
  type: 'message' | 'meeting' | 'achievement' | 'assignment' | 'announcement';
  title: string;
  message: string;
  isRead: boolean;
  priority: 'low' | 'normal' | 'high';
  actionUrl?: string;
  fromUserId?: number;
  fromUserName?: string;
  fromUserAvatar?: string;
  metadata?: string;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationSummary {
  totalNotifications: number;
  unreadCount: number;
  unreadByType?: Record<string, number>;
  recentNotifications: Notification[];
}

export interface CreateNotificationRequest {
  userId: number;
  type: string;
  title: string;
  message: string;
  priority?: string;
  actionUrl?: string;
  fromUserId?: number;
  metadata?: string;
}

class NotificationService {
  /**
   * Get all notifications for the current user
   */
  async getNotifications(): Promise<Notification[]> {
    const response = await studentApi.get('/api/notifications/current');
    return response.data.data;
  }

  /**
   * Get unread notifications for the current user
   */
  async getUnreadNotifications(): Promise<Notification[]> {
    const response = await studentApi.get('/api/notifications/current/unread');
    return response.data.data;
  }

  /**
   * Get notification summary for the current user
   */
  async getNotificationSummary(): Promise<NotificationSummary> {
    const response = await studentApi.get('/api/notifications/current/summary');
    return response.data.data;
  }

  /**
   * Get paginated notifications for the current user
   */
  async getPaginatedNotifications(page: number = 0, size: number = 10): Promise<{
    content: Notification[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
  }> {
    const response = await studentApi.get(`/api/notifications/current/paginated?page=${page}&size=${size}`);
    return response.data.data;
  }

  /**
   * Mark a notification as read
   */
  async markAsRead(notificationId: number): Promise<void> {
    await studentApi.put(`/api/notifications/${notificationId}/read`);
  }

  /**
   * Mark all notifications as read for the current user
   */
  async markAllAsRead(): Promise<void> {
    await studentApi.put('/api/notifications/current/read-all');
  }

  /**
   * Create a new notification
   */
  async createNotification(request: CreateNotificationRequest): Promise<Notification> {
    const response = await studentApi.post('/api/notifications', request);
    return response.data.data;
  }

  /**
   * Delete a notification
   */
  async deleteNotification(notificationId: number): Promise<void> {
    await studentApi.delete(`/api/notifications/${notificationId}`);
  }

  /**
   * Clear all notifications for the current user
   */
  async clearAllNotifications(): Promise<void> {
    await studentApi.delete('/api/notifications/current/clear-all');
  }

  /**
   * Get notifications by type for the current user
   */
  async getNotificationsByType(type: string): Promise<Notification[]> {
    const response = await studentApi.get(`/api/notifications/current/type/${type}`);
    return response.data.data;
  }
}

export const notificationService = new NotificationService();
export default notificationService;
